<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import type { GenOrder, IOrderResponse } from '../order'
    import * as yup from 'yup'

    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import type { TGettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const Order = getContext('Order') as GenOrder

    const status = sderived(Order.status, ($status) => $status)
    const fullyCompleted = sderived(Order.fullyCompleted, ($fullyCompleted) => $fullyCompleted)

    let aStatus: 'ready' | 'loading' = $state('loading' as 'ready' | 'loading')
    let askConfirmation: boolean = $state(false)
    let emailing: boolean = $state(false)

    let order = sderived(Order.orderStore, ($order) => $order)

    interface EmailAddress {
        id: string
        email: string
        isMain: boolean
        isDefaultBilling: boolean
        title: string | TGettext
    }

    let addresses: EmailAddress[] = $state([])
    let loadedAddressesUserId: string

    async function loadAddresses() {
        if ($order.owner == loadedAddressesUserId) return
        aStatus = 'loading'

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/wdom-user/1/${$order.owner}/addresses`)
        } catch (e: any) {
            // Just be silent for now
            addresses = []
            r = e
        }
        if (r) {
            loadAddressesData(r.data)
        }
        loadedAddressesUserId = $order.owner
        aStatus = 'ready'
    }

    function loadAddressesData(data: any) {
        let laddresses: any = []
        for (const [i, ladd] of data.addresses.entries()) {
            if (!ladd.email) continue
            let cgroup: EmailAddress = {
                email: ladd.email,
                isMain: i == data.base_index,
                isDefaultBilling: i == data.billing_index,
                title: ladd.title,
                id: ladd.id,
            }
            if (cgroup.isMain) {
                cgroup.title = tgettext('Account address')
                cgroup.id = 'main'
            }

            laddresses.push(cgroup)
        }
        addresses = laddresses
    }

    unsubs.push(
        order.subscribe(($order) => {
            if ($order) loadAddresses()
        }),
    )

    let emailAddress: string = $derived($status == 'ready' && aStatus == 'ready' && addresses.length ? addresses[0].email : '')
    let emailSchema = yup.string().email().required()
    let emailIsValid = $derived(emailSchema.isValidSync(emailAddress))

    async function sendEmail() {
        emailing = true
        askConfirmation = false

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('POST', `/json/ecom/1/order/${$order.id}/send-by-email/${emailAddress}`)
        } catch (e: any) {
            console.error('Could not send email:', e)
            r = e
            notify({
                type: 'danger',
                title: 'Envoi d’un courriel',
                text: 'Le courriel n’a pas pu être envoyé.',
            })
        }

        if (r) {
            Order.raw.order = (r.data as IOrderResponse).order
            Order.raw.completion = (r.data as IOrderResponse).completion
            Order.importOrderFromRaw()
            notify({
                type: 'success',
                title: 'Envoi d’un courriel',
                text: 'Le courriel a été envoyé.',
            })
        }

        emailing = false
    }

    $effect(() => {
        if (askConfirmation && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!askConfirmation && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (askConfirmation && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:body
    onclick={(e) => {
        if (askConfirmation) askConfirmation = false
    }}
/>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (askConfirmation) askConfirmation = false
        }
    }}
/>

<div class="card">
    <div class="card-title text-primary">{$_('Envoi d’un courriel')}</div>
    {#if $status != 'ready' || aStatus == 'loading'}
        <div class="card-body">
            <LoadingPattern pattern={['x', 'x']} />
        </div>
    {:else}
        <div class="card-body">
            <form
                novalidate
                autocomplete="off"
                onsubmit={(e) => {
                    e.preventDefault()
                    askConfirmation = true
                }}
            >
                <p>
                    <input type="email" class="form-control" bind:value={emailAddress} disabled={emailing} />
                </p>
                {#if addresses.length}
                    <ul style="padding-left: 1em;">
                        {#each addresses as addr}
                            <li>
                                <button type="button" class="btn btn-link btn-a" disabled={addr.email == emailAddress} onclick={() => (emailAddress = addr.email)}>{addr.email}</button>
                                {#if addr.isMain}
                                    <span style="font-size: 0.7em; vertical-align: middle; cursor: help;" class="tt-left tt-below" data-tooltip="Adresse principale">[P]</span>
                                {/if}
                                {#if addr.isDefaultBilling}
                                    <span style="font-size: 0.7em; vertical-align: middle; cursor: help;" class="tt-left tt-below" data-tooltip="Adresse de facturation">[F]</span>
                                {/if}
                            </li>
                        {/each}
                    </ul>
                {/if}

                <p class="d-grid">
                    {#if emailing}
                        <button type="button" class="btn btn-info"
                            ><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            {$_('Un moment…')}</button
                        >
                    {:else}
                        <button type="submit" class="btn btn-outline-secondary" disabled={!emailIsValid || !$fullyCompleted}>{$_('Envoyer par courriel')}</button>
                    {/if}
                </p>
            </form>
        </div>
    {/if}
</div>

{#if askConfirmation}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Envoi de la facture par courriel')}</div>
                </div>
                <div class="modal-body">
                    <p>Ceci enverra un email à “{emailAddress}” contenant un lien pour voir (et payer) la facture.</p>
                    {#if $order.status == 'draft'}
                        <p>La commande passera de <em>draft</em> à <em>ready</em>.</p>
                    {/if}
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" onclick={() => (askConfirmation = false)}>{$_('Cancel')}</button>
                    <button type="button" class="btn btn-outline-secondary" onclick={() => sendEmail()}>{$_('Envoyer à : {email}', { email: emailAddress })}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (askConfirmation = false)} aria-hidden="true"></div>
    </div>
{/if}
